import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import * as moment from 'moment-timezone';
import { Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import LoggedAction from 'src/app/model/logged-action';
import Board from 'src/app/model/product/board/board';
import BoardPlacement from 'src/app/model/product/board/board-placement';
import Employee from 'src/app/model/product/employee';
import { ProjectPlacement } from 'src/app/model/project/projectPlacement';
import { AuthService } from 'src/app/services/auth.service';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import { ModalService } from 'src/app/services/modal.service';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { SubProject } from '../../../../model/project/sub-project';

@Component({
  selector: 'board-placement-form',
  templateUrl: './board-placement-form.component.html',
  styleUrls: ['./board-placement-form.component.scss'],
})
export class BoardPlacementFormComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit
{
  @ViewChild('actualPlacementInput')
  public actualPlacementInput: ElementRef<HTMLInputElement>;

  @Input()
  public inputProject: SubProject;
  @Input()
  public inputProduct: Board;
  @Input()
  public inputProjectPlacement: ProjectPlacement;
  @Output()
  public outputProduct = new EventEmitter();

  public extraInfoSiteChangeSub = new Subject<string>();

  public hours: string[] = [];
  public minutes: string[] = [];
  public placement: BoardPlacement;
  public employeeChoices: Employee[] = [];
  public externalSupplierEmployeeChoices: Employee[];

  requestedDateDisplay: string;
  showNearbyParkingQuestion: boolean = false;
  invoiceDisables: boolean;
  nbInstallersRequired: number;

  DEFAULT_EXECUTION_TIME = '00:00';
  executionTime = this.DEFAULT_EXECUTION_TIME;

  constructor(
    private authService: AuthService,
    private datePipe: DatePipe,
    private modalService: ModalService,
    private employeeChoicesService: EmployeeChoicesService,
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit() {
    if (this.inputProduct.productPlacement != null) {
      this.placement = <BoardPlacement>this.inputProduct.productPlacement;
    } else {
      this.placement = new BoardPlacement();
      this.placement.productPlacementType = 'BoardPlacement';
      this.placement.kilometersTo = this.inputProjectPlacement.kilometersTo;
      this.inputProduct.productPlacement = this.placement;
    }
    if (
      !!this.inputProduct &&
      !!this.inputProduct.productPlacement &&
      !!this.inputProduct.productPlacement.executionTime
    ) {
      this.executionTime =
        this.inputProduct.productPlacement.executionTime.slice(0, 5);
    }
    this.hours = ['0', '1', '2', '3', '4', '5', '6', '7', '8'];
    this.minutes = Array(60)
      .fill(1)
      .map((x, y) => String(x + y - 1).padStart(2, '0'));
    this.initOverrideableFields();
    this.getEmployees();
    this.calculateRequestedDateDisplay();
    this.calculateShowNearbyParkingQuestion();
    this.calculateSignSizeRequirements();
    this.calculateInvoiceDisables();

    const calculateSignSizeRequirementsObservable =
      this.createObservableUntilDestroy(
        this.projectDisplayObservableService.getProductPartDeletedSubscribable(),
        this.projectDisplayObservableService.getProductSizeChangedSubscribable()
      );

    this.addSubscription(
      this.projectDisplayObservableService.getProjectPlacementRequestDateChangedSubscribable(),
      () => {
        this.calculateRequestedDateDisplay();
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProductPlacementVariableChangedSubscribable(),
      () => {
        this.calculateShowNearbyParkingQuestion();
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getInvoiceSentSubscribable(),
      () => {
        this.calculateInvoiceDisables();
      }
    );
    this.addSubscription(calculateSignSizeRequirementsObservable, () =>
      this.calculateSignSizeRequirements()
    );
  }

  initOverrideableFields() {
    if (this.inputProduct.productPlacement.extraInfoSite == null) {
      this.inputProduct.productPlacement.extraInfoSite =
        this.inputProjectPlacement.extraInfoSite;
    }
  }

  getEmployees() {
    this.addSubscription(
      this.employeeChoicesService.getPlacerChoicesSubscribable(),
      (result) => {
        this.employeeChoices = <Employee[]>result;
      }
    );
    this.addSubscription(
      this.employeeChoicesService.getExternalSupplierChoicesSubscribable(),
      (result) => {
        this.externalSupplierEmployeeChoices = <Employee[]>result;
      }
    );
  }

  placeInstructionsChange(value) {
    this.inputProduct.placeInstructions = value;
    this.outputProduct.emit();
  }

  distanceFrontPostToStreetChanged(value) {
    this.inputProduct.distanceFrontPostToStreet = value;
    this.outputProduct.emit();
  }

  installerInfoChange(value) {
    this.inputProduct.productPlacement.installerInfo = value;
    this.outputProduct.emit();
  }

  newWoodPostsChange(value) {
    this.placement.newWoodPosts = value;
    this.outputProduct.emit();
  }

  obstaclesPresentChange(value) {
    this.placement.obstaclesPresent = value;
    this.outputProduct.emit();
  }

  obstaclesInfoTextChange(value) {
    this.placement.obstaclesInfo = value;
    this.outputProduct.emit();
  }

  placerChange() {
    this.projectDisplayObservableService.getPlacersChangedSubscribable().next();
    this.outputProduct.emit();
  }

  setHours() {
    this.outputProduct.emit();
  }

  setMinutes() {
    this.outputProduct.emit();
  }

  compareEnums(e1, e2) {
    return e1 != null && e2 != null && e1.name === e2.name;
  }

  compareEntities(o1, o2) {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      if (o1.id === o2.id) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  compareNumbers(n1, n2) {
    return n1 == n2;
  }

  getChosenPlacers() {
    if (
      this.inputProduct.productPlacement == null ||
      this.inputProduct.productPlacement.placers == null ||
      this.inputProduct.productPlacement.placers.length === 0
    ) {
      return 'Geen medewerkers gekozen';
    }

    let toReturn = '';
    for (
      let i = 0;
      i < this.inputProduct.productPlacement.placers.length;
      i++
    ) {
      let placer = this.inputProduct.productPlacement.placers[i];
      if (placer.user == null) {
        continue;
      }
      if (i > 0) {
        toReturn += ', ' + placer.user.firstName + ' ' + placer.user.lastName;
      } else {
        toReturn += placer.user.firstName + ' ' + placer.user.lastName;
      }
    }
    return toReturn;
  }

  hoogwerkerChanged(event) {
    this.inputProduct.productPlacement.extraHoogwerker = event;
    this.outputProduct.emit();
  }

  hoogwerkerTextChanged(event) {
    this.inputProduct.productPlacement.extraHoogwerkerDescription = event;
    this.outputProduct.emit();
  }

  hoogwerkerDoneChanged(event) {
    this.inputProduct.productPlacement.extraHoogwerkerDone = event;
    this.outputProduct.emit();
  }

  extraHoogwerkerEmployeeChange() {
    this.outputProduct.emit();
  }

  extraHoogwerkerDeadlineChange(event) {
    this.inputProduct.productPlacement.extraHoogwerkerDeadline = event.value;
    this.outputProduct.emit();
  }

  placeASAP(event) {
    this.inputProduct.productPlacement.placeASAP = event;
    if (event) {
      delete this.inputProduct.productPlacement.actualPlacementDate;
    }
    this.outputProduct.emit();
  }

  actualPlacementDateChange(event) {
    let newDate = event.value;
    if (
      this.inputProjectPlacement.placementEarliestDate != null &&
      moment
        .tz(newDate, 'Europe/Brussels')
        .isBefore(
          moment.tz(
            this.inputProjectPlacement.placementEarliestDate,
            'Europe/Brussels'
          ),
          'day'
        )
    ) {
      this.modalService.confirmDialog(
        'Datum vroeger dan gevraagde datum',
        'De geselecteerde datum (' +
          DateHelper.formatDate(newDate) +
          ') is vroeger dan de gevraagde datum (' +
          DateHelper.formatDate(
            this.inputProjectPlacement.placementEarliestDate
          ) +
          '). Bent u zeker dat u deze datum wilt selecteren?',
        (c) => {
          if (c) {
            if (
              this.inputProjectPlacement.appointment &&
              this.inputProduct.productPlacement.actualPlacementDate != null
            ) {
              this.checkAppointmentPlacement(newDate);
            } else {
              this.inputProduct.productPlacement.actualPlacementDate = newDate;
              this.outputProduct.emit();
            }
          } else {
            this.actualPlacementInput.nativeElement.value =
              DateHelper.formatDate(
                this.inputProduct.productPlacement.actualPlacementDate
              );
          }
        }
      );
    } else if (
      this.inputProjectPlacement.appointment &&
      this.inputProduct.productPlacement.actualPlacementDate != null
    ) {
      this.checkAppointmentPlacement(newDate);
    } else {
      this.inputProduct.productPlacement.actualPlacementDate = newDate;
      this.outputProduct.emit();
    }
  }

  checkAppointmentPlacement(newDate) {
    this.modalService.confirmDialog(
      'Opgelet!',
      'Er is een afspraak nodig voor deze plaatsing. Contacteer de klant om deze ook te verzetten indien u de plaatsingsdatum aanpast.',
      (c) => {
        if (c) {
          this.inputProduct.productPlacement.actualPlacementDate = newDate;
          this.outputProduct.emit();
        } else {
          this.actualPlacementInput.nativeElement.value = DateHelper.formatDate(
            this.inputProduct.productPlacement.actualPlacementDate
          );
        }
      }
    );
  }

  actualPlacementDateConfirmedChange(value) {
    this.inputProduct.productPlacement.actualPlacementDateConfirmed = value;
    this.outputProduct.emit();
  }

  kilometersToChanged() {
    this.outputProduct.emit();
  }

  installerFeedbackChange(event) {
    this.inputProduct.productPlacement.installerFeedback = event;
    this.outputProduct.emit();
  }

  executionCompleteChange(event) {
    this.inputProduct.productPlacement.executionComplete = event;
    if (event) {
      this.inputProduct.productPlacement.executionDate = new Date();
      let hours =
        this.inputProduct.productPlacement.executionDate.getHours() < 10
          ? '0' + this.inputProduct.productPlacement.executionDate.getHours()
          : this.inputProduct.productPlacement.executionDate.getHours();
      let minutes =
        this.inputProduct.productPlacement.executionDate.getMinutes() < 10
          ? '0' + this.inputProduct.productPlacement.executionDate.getMinutes()
          : this.inputProduct.productPlacement.executionDate.getMinutes();

      this.executionTimeChange(hours + ':' + minutes);
      this.inputProduct.productPlacement.placedRegistration =
        new LoggedAction();
      this.inputProduct.productPlacement.placedRegistration.date = new Date();
      this.inputProduct.productPlacement.placedRegistration.by =
        this.authService.getAuthenticatedUser();
    } else {
      this.inputProduct.productPlacement.executionDate = null;
      this.inputProduct.productPlacement.executionTime = null;
      this.inputProduct.productPlacement.placedRegistration = null;
    }
    this.projectDisplayObservableService
      .getProductExecutedSubscribable()
      .next();
    this.outputProduct.emit();
  }

  executionCompleteInfoChange(event) {
    this.inputProduct.productPlacement.executionCompleteInfo = event;
    this.outputProduct.emit();
  }

  parkingBanChanged(value) {
    this.inputProduct.productPlacement.parkingBan = value;
    this.outputProduct.emit();
  }

  extraInfoParkingBanChanged(value) {
    this.inputProduct.productPlacement.extraInfoParkingBan = value;
    this.outputProduct.emit();
  }

  ParkingBanDoneChanged(value) {
    this.inputProduct.productPlacement.parkingBanDone = value;
    this.outputProduct.emit();
  }

  parkingBanEmployeeChange() {
    this.outputProduct.emit();
  }

  parkingBanDeadlineChange(event) {
    this.inputProduct.productPlacement.parkingBanDeadline = event.value;
    this.outputProduct.emit();
  }

  needSiteVisitChange(value) {
    this.inputProduct.productPlacement.needSiteVisit = value;
    this.outputProduct.emit();
  }

  extraInfoNeedSiteVisitChange(value) {
    this.inputProduct.productPlacement.extraInfoNeedSiteVisit = value;
    this.outputProduct.emit();
  }

  needExtraSupplierChange(value) {
    this.inputProduct.productPlacement.needExtraSupplier = value;
    this.outputProduct.emit();
  }

  needExtraSupplierDoneChange(value) {
    this.inputProduct.productPlacement.needExtraSupplierDone = value;
    this.outputProduct.emit();
  }

  extraInfoNeedExtraSupplierChange(value) {
    this.inputProduct.productPlacement.extraInfoNeedExtraSupplier = value;
    this.outputProduct.emit();
  }

  extraSupplierEmployeeChange() {
    this.outputProduct.emit();
  }

  extraSupplierDeadlineChange(event) {
    this.inputProduct.productPlacement.extraSupplierDeadline = event.value;
    this.outputProduct.emit();
  }

  needExtraMaterialsChange(value) {
    this.inputProduct.productPlacement.needExtraMaterials = value;
    this.outputProduct.emit();
  }

  needExtraMaterialsDoneChange(value) {
    this.inputProduct.productPlacement.needExtraMaterialsDone = value;
    this.outputProduct.emit();
  }

  extraInfoNeedExtraMaterialsChange(value) {
    this.inputProduct.productPlacement.extraInfoNeedExtraMaterials = value;
    this.outputProduct.emit();
  }

  extraMaterialsEmployeeChange() {
    this.outputProduct.emit();
  }

  extraMaterialsDeadlineChange(event) {
    this.inputProduct.productPlacement.extraMaterialsDeadline = event.value;
    this.outputProduct.emit();
  }

  executionDateChange() {
    this.outputProduct.emit();
  }

  executionTimeChange(value) {
    this.inputProduct.productPlacement.executionTime = value;
    this.outputProduct.emit();
  }

  canParkNearbyChanged(value: boolean) {
    this.placement.canParkNearby = value;
    this.outputProduct.emit();
  }

  nearbyParkingInfoChanged(value: string) {
    this.placement.nearbyParkingInfo = value;
    this.outputProduct.emit();
  }

  calculateInvoiceDisables(): boolean {
    if (this.inputProject.projectInvoices == null) {
      this.invoiceDisables = false;
      return;
    }
    for (let i of this.inputProject.projectInvoices) {
      if (i.type === 'PlacementInvoice' && i.sentDate != null) {
        this.invoiceDisables = true;
        return;
      }
    }
    this.invoiceDisables = false;
  }

  calculateRequestedDateDisplay() {
    if (this.inputProject.projectPlacement == null) {
      this.requestedDateDisplay = 'Geen';
      return;
    }
    if (this.inputProject.projectPlacement.placementEarliestDate == null) {
      this.requestedDateDisplay = this.inputProjectPlacement.placeASAP
        ? 'ASAP'
        : 'Geen';
      return;
    }
    this.requestedDateDisplay = this.datePipe.transform(
      this.inputProjectPlacement.placementEarliestDate,
      'dd/MM/yyyy'
    );
  }

  calculateShowNearbyParkingQuestion() {
    this.showNearbyParkingQuestion = CollectionHelper.isPopulated(
      this.inputProduct.boardParts
    )
      ? this.inputProduct.boardParts.some((bp) => {
          return bp.placementType != null && bp.placementType.ordinal === 3;
        })
      : false;
  }

  calculateSignSizeRequirements() {
    if (!CollectionHelper.isPopulated(this.inputProduct.boardParts)) {
      this.nbInstallersRequired = null;
      return;
    }

    let nbInstallersRequired = 0;
    let totalPlacerMinutes = 0;

    this.inputProduct.boardParts.forEach((bp) => {
      if (bp.signSize != null) {
        nbInstallersRequired =
          bp.signSize.nbInstallersRequired > nbInstallersRequired
            ? bp.signSize.nbInstallersRequired
            : nbInstallersRequired;
        totalPlacerMinutes +=
          bp.signSize.placerMinutes == null ? 0 : bp.signSize.placerMinutes;
      }
    });

    this.nbInstallersRequired =
      nbInstallersRequired === 0 ? null : nbInstallersRequired;
    if (
      this.inputProduct.productPlacement.installerHourBudget == null &&
      this.inputProduct.productPlacement.installerMinuteBudget == null
    ) {
      const [hours, minutes] =
        GeneralHelper.getMinutesInHourAndMinutesArray(totalPlacerMinutes);
      this.inputProduct.productPlacement.installerHourBudget = hours;
      this.inputProduct.productPlacement.installerMinuteBudget = minutes;
    }
  }

  copyProjectPlacementDate() {
    this.inputProduct.productPlacement.actualPlacementDate = new Date(
      this.inputProjectPlacement.placementEarliestDate
    );
    this.outputProduct.emit();
  }

  ordinalEnumGetter(o: any): string {
    return GeneralHelper.ordinalEnumGetter(o);
  }

  compareOrdinalEnum(o1: any, o2: any): boolean {
    return GeneralHelper.compareOrdinalEnum(o1, o2);
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
